import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Row,
  Col,
  Badge,
  Form,
  OverlayTrigger,
  Popover,
  Tooltip,
} from "react-bootstrap";
import { FiInfo } from "react-icons/fi";
import {
  Calendar,
  Search,
  Box,
  Truck,
  CheckCircle,
  Clock,
  ArrowRight,
  FileText,
  Layers,
  X,
} from "lucide-react";

const SalesChart = () => {
  const [urgentOrders, setUrgentOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("TUTTI");
  const [tempDates, setTempDates] = useState({ start: "", end: "" });
  const [appliedDates, setAppliedDates] = useState({ start: "", end: "" });
  const [showModal, setShowModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const ordiniRaw = json?.STATO_ORDINI?.flat() || [];
        const sorted = ordiniRaw.sort((a, b) =>
          String(a["Num Doc"]).localeCompare(String(b["Num Doc"])),
        );
        setUrgentOrders(sorted);
      })
      .catch((err) => console.error("Errore nel caricamento dati:", err));
  }, []);

  const getStatusStyle = (stato) => {
    const map = {
      "IN RIMESSO": { bg: "#fff5f5", color: "#e03131", icon: <Clock size={12} /> },
      "IN MAGAZZINO": { bg: "#e6fcf5", color: "#099268", icon: <Box size={12} /> },
      "PRESO IN CARICO": { bg: "#e8f7ff", color: "#0066cc", icon: <Layers size={12} /> },
      "BEM INVIATA": { bg: "#fff9db", color: "#f08000", icon: <FileText size={12} /> },
      "BEM COMPLETATA": { bg: "#fff0f6", color: "#d6336c", icon: <CheckCircle size={12} /> },
      "INVIO ODP/ODPV": { bg: "#f3f0ff", color: "#6741d9", icon: <Truck size={12} /> },
      "COMPLETAMENTO ODP/ODPV": { bg: "#edf2ff", color: "#364fc7", icon: <CheckCircle size={12} /> },
      "CHIUSO CON BOLLA": { bg: "#e7f5ff", color: "#1c7ed6", icon: <FileText size={12} /> },
      "GESTITO": { bg: "#f3f0ff", color: "#6741d9", icon: <CheckCircle size={12} /> },
      "COMPLETATO": { bg: "#ebfbee", color: "#2f9e44", icon: <CheckCircle size={12} /> },
    };

    return map[stato] || { bg: "#f8f9fa", color: "#495057", icon: <Layers size={12} /> };
  };

  const getDelayBadge = (dateString) => {
    const orderDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - orderDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const badgeStyle = { fontSize: "0.65rem", padding: "6px 10px" };

    if (diffDays <= 3) {
      return <Badge bg="success" style={badgeStyle}>IN ORARIO</Badge>;
    } else if (diffDays <= 7) {
      return <Badge bg="warning" text="dark" style={badgeStyle}>ATTENZIONE</Badge>;
    } else {
      return <Badge bg="danger" style={badgeStyle}>IN RITARDO</Badge>;
    }
  };

  const filteredOrders = urgentOrders.filter((order) => {
    if (order.STATO === "COMPLETATO") return false;

    const orderDate = new Date(order["Data Ordine"]).toISOString().split("T")[0];
    const s = searchTerm.toLowerCase();

    const matchesSearch =
      order["Num Doc"]?.toString().toLowerCase().includes(s) ||
      order["Articolo"]?.toString().toLowerCase().includes(s) ||
      order["Ragione sociale"]?.toString().toLowerCase().includes(s) ||
      order["Rag Soc Cli"]?.toString().toLowerCase().includes(s) ||
      order["STATO"]?.toString().toLowerCase().includes(s);

    const matchesStatus = statusFilter === "TUTTI" || order.STATO === statusFilter;
    const matchesStart = appliedDates.start === "" || orderDate >= appliedDates.start;
    const matchesEnd = appliedDates.end === "" || orderDate <= appliedDates.end;

    return matchesSearch && matchesStatus && matchesStart && matchesEnd;
  });

  const renderTimelineStep = (label, date) => {
    const isDone = date && date !== "null" && date !== null;

    return (
      <Col className="text-center px-1">
        <div style={{ fontSize: "0.58rem", fontWeight: "800", color: isDone ? "#0d6efd" : "#adb5bd", textTransform: "uppercase", marginBottom: "8px", minHeight: "28px" }}>
          {label}
        </div>
        <div style={{ height: "4px", backgroundColor: isDone ? "#0d6efd" : "#e9ecef", borderRadius: "2px", marginBottom: "8px", position: "relative" }}>
          {isDone && (
            <div style={{ position: "absolute", top: "-4px", left: "50%", width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#0d6efd", transform: "translateX(-50%)" }}></div>
          )}
        </div>
        <div style={{ fontSize: "0.65rem", color: isDone ? "#212529" : "#dee2e6", fontWeight: isDone ? "600" : "400" }}>
          {isDone ? new Date(date).toLocaleDateString("it-IT") : "--/--"}
        </div>
      </Col>
    );
  };

  // Contenuto dei filtri condivisibile tra card normale e versione full screen
  const renderFilters = () => (
    <div className="d-flex gap-2 align-items-center">
      <OverlayTrigger
        trigger="click"
        placement="bottom"
        rootClose
        overlay={
          <Popover className="shadow-lg border-0 p-3" style={{ borderRadius: "12px", zIndex: 1060 }}>
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold">Intervallo Ordine</Form.Label>
              <Form.Control type="date" size="sm" className="mb-2" value={tempDates.start} onChange={(e) => setTempDates({ ...tempDates, start: e.target.value })} />
              <Form.Control type="date" size="sm" value={tempDates.end} onChange={(e) => setTempDates({ ...tempDates, end: e.target.value })} />
            </Form.Group>
            <Button variant="primary" size="sm" className="w-100" onClick={() => { setAppliedDates(tempDates); document.body.click(); }}>
              Applica
            </Button>
          </Popover>
        }
      >
        <Button variant="light" className="border-0 shadow-sm" style={{ borderRadius: "10px", padding: "8px 12px" }}>
          <Calendar size={16} className="text-primary" />
        </Button>
      </OverlayTrigger>

      <Form.Select
        size="sm"
        className="border-0 shadow-sm"
        style={{ borderRadius: "10px", width: "220px", height: "38px", fontSize: "0.8rem", fontWeight: "600" }}
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="TUTTI">Tutti gli stati ({filteredOrders.length})</option>
        <option value="IN RIMESSO">1. IN RIMESSO</option>
        <option value="BEM INVIATA">4. BEM INVIATA</option>
        <option value="BEM COMPLETATA">5. BEM COMPLETATA</option>
        <option value="INVIO ODP/ODPV">6. INVIO ODP/ODPV</option>
        <option value="COMPLETAMENTO ODP/ODPV">7. COMPL. ODP/ODPV</option>
        <option value="CHIUSO CON BOLLA">8. CHIUSO CON BOLLA</option>
      </Form.Select>

      <div className="position-relative">
        <Search size={16} className="position-absolute" style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#adb5bd" }} />
        <Form.Control
          size="sm"
          className="border-0 shadow-sm"
          placeholder="Cerca art., doc, cliente, stato..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ borderRadius: "10px", width: "240px", paddingLeft: "38px", height: "38px", fontSize: "0.8rem" }}
        />
      </div>
    </div>
  );

  // Contenuto della tabella condivisibile
  const renderTableContent = () => (
    <Table className="custom-table" response="true">
      <thead>
        <tr className="text-muted" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "1px" }}>
          <th>Cod. Articolo</th>
          <th>Descrizione Componente</th>
          <th>Data Ordine</th>
          <th>Stato Avanzamento (SQL)</th>
          <th className="text-end">Priorità</th>
        </tr>
      </thead>
      <tbody>
        {filteredOrders.map((ordine, index) => {
          const showHeader = index === 0 || ordine["Num Doc"] !== filteredOrders[index - 1]["Num Doc"];
          const currentStyle = getStatusStyle(ordine.STATO);

          return (
            <React.Fragment key={index}>
              {showHeader && (
                <tr className="doc-header-row">
                  <td colSpan={5}>
                    <div className="d-flex align-items-center justify-content-between" style={{ fontSize: "0.75rem", fontWeight: "600" }}>
                      <div>
                        <Badge bg="dark" className="me-2" style={{ fontSize: "0.65rem" }}>DOC n° {ordine["Num Doc"]}</Badge>
                        <span className="text-dark"><small className="text-muted fw-normal">Fornitore:</small> {ordine["Ragione sociale"]}</span>
                      </div>
                      <span className="text-dark"><small className="text-muted fw-normal">Destinatario:</small> {ordine["Rag Soc Cli"]}</span>
                    </div>
                  </td>
                </tr>
              )}

              <tr className="item-row shadow-sm" onClick={() => { setSelectedOrder(ordine); setShowModal(true); }}>
                <td><div className="fw-bold text-primary" style={{ fontSize: "0.8rem" }}>{ordine["Articolo"]}</div></td>
                <td style={{ fontSize: "0.78rem", color: "#333", maxWidth: "350px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ordine["Descrizione"]}</td>
                <td style={{ fontSize: "0.78rem" }}>{new Date(ordine["Data Ordine"]).toLocaleDateString("it-IT")}</td>
                <td>
                  <div className="status-badge" style={{ backgroundColor: currentStyle.bg, color: currentStyle.color, borderRadius: "6px" }}>
                    {currentStyle.icon} {ordine.STATO}
                  </div>
                </td>
                <td className="text-end">{getDelayBadge(ordine["Data Ordine"])}</td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );

  return (
    <div className="bg-white shadow-sm" style={{ borderRadius: "16px", maxHeight: "400px", display: "flex", flexDirection: "column", overflow: "hidden", border: "1px solid #eee" }}>
      
      {/* HEADER E FILTRI */}
      <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span style={{ fontWeight: "800", fontSize: "0.75rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
            MONITORAGGIO FLUSSI ARTICOLI
          </span>


        </div>

        {renderFilters()}
      </div>

      <style>{`
        .scroll-area { overflow-y: auto; flex-grow: 1; padding: 15px; background-color: #f8f9fa; }
        .custom-table { border-collapse: separate; border-spacing: 0 6px; }
        .doc-header-row td { background: #e9ecef !important; border: none !important; border-radius: 6px; padding: 8px 15px !important; }
        .item-row { cursor: pointer; transition: all 0.15s; }
        .item-row td { background: white !important; border: none !important; padding: 12px 15px !important; vertical-align: middle; }
        .item-row:hover td { background: #f1f7fe !important; }
        .item-row td:first-child { border-top-left-radius: 10px; border-bottom-left-radius: 10px; }
        .item-row td:last-child { border-top-right-radius: 10px; border-bottom-right-radius: 10px; }
        .status-badge { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; font-weight: 700; font-size: 0.65rem; text-transform: uppercase; }
      `}</style>

      {/* TABELLA REGOLARE */}
      <div className="scroll-area">
        {renderTableContent()}
      </div>

      {/* MODALE TUTTO SCHERMO (DASHBOARD COMPLETA) */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center shadow-sm">
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                MONITORAGGIO FLUSSI ARTICOLI - VISTA ESPANSA
              </span>
            </div>
            <div className="d-flex gap-3 align-items-center">
              {renderFilters()}
              <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
                <X size={16} /> Chiudi
              </Button>
            </div>
          </div>
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="bg-white p-3 shadow-sm rounded-4 border">
              {renderTableContent()}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODAL DETTAGLIO ARTICOLO (ORDINATA -> RIMESSA -> EVASA) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered style={{ zIndex: 1070 }} className="p-5 rounded">
        <Modal.Body className="p-0">
          <div className="p-4 border-bottom bg-light d-flex justify-content-between align-items-center">
            <div>
              <div className="text-primary fw-bold" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Tracciabilità Stato Articolo</div>
              <h5 className="mb-0 fw-bold text-dark">{selectedOrder?.Articolo} <span className="fw-normal text-muted" style={{ fontSize: "0.9rem" }}>- {selectedOrder?.Descrizione}</span></h5>
            </div>
            <Button variant="outline-secondary" size="sm" style={{ borderRadius: "8px" }} onClick={() => setShowModal(false)}>Chiudi</Button>
          </div>

          <div className="p-4 bg-white">
            <div className="p-3 bg-light rounded-4 mb-4 border">
              <Row className="g-0">
                {selectedOrder && (
                  <>
                    {renderTimelineStep("1. IN RIMESSO", selectedOrder["Data Ordine"])}
                    {renderTimelineStep("2. In Magazzino", selectedOrder["Data Entrata Magazzino"] || selectedOrder["Data Entrata Magazzino Acc"])}
                    {renderTimelineStep("3. In Carico", selectedOrder["Data Presa in carico *"] || selectedOrder["Data Presa in carico Acc"])}
                    {renderTimelineStep("4. BEM Inviata", selectedOrder["Data Invio Bem"])}
                    {renderTimelineStep("5. BEM Compl.", selectedOrder["Data Completamento BEM"])}
                    {renderTimelineStep("6. Invio ODP", selectedOrder["Data Creazione ODP"])}
                    {renderTimelineStep("7. Compl. ODP", selectedOrder["Data Chiusura ODP"])}
                    {renderTimelineStep("8. Bolla", selectedOrder["data_bolla"])}
                    {renderTimelineStep("9. Completato", selectedOrder["data_fatt"])}
                  </>
                )}
              </Row>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="p-3 rounded-3 bg-light h-100 border text-muted" style={{ fontSize: "0.8rem" }}>
                  <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "0.85rem" }}>Riferimenti Anagrafici</h6>
                  <div className="d-flex justify-content-between border-bottom py-1"><span>Documento d'Acquisto:</span><span className="fw-bold text-dark">#{selectedOrder?.["Num Doc"]}</span></div>
                  <div className="d-flex justify-content-between border-bottom py-1"><span>Intestatario Fornitore:</span><span className="fw-bold text-dark">{selectedOrder?.["Ragione sociale"]}</span></div>
                  <div className="d-flex justify-content-between py-1"><span>Cliente Finale:</span><span className="fw-bold text-dark">{selectedOrder?.["Rag Soc Cli"]}</span></div>
                </div>
              </Col>

              <Col md={6}>
                <div className="p-3 rounded-3 border h-100 d-flex align-items-center justify-content-around text-center bg-white shadow-sm">
                  <div>
                    <div className="text-muted small">Ordinata</div>
                    <div className="h3 fw-bold text-dark mb-0">{parseInt(selectedOrder?.Quantita || 0, 10)}</div>
                  </div>
                  <ArrowRight className="text-muted" size={16} />
                  <div>
                    <div className="text-warning small">Rimesso</div>
                    <div className="h3 fw-bold text-warning mb-0">{parseInt(selectedOrder?.["Qta Rimesso"] || 0, 10)}</div>
                  </div>
                  <ArrowRight className="text-muted" size={16} />
                  <div>
                    <div className="text-success small">Evasi</div>
                    <div className="h3 fw-bold text-success mb-0">{parseInt(selectedOrder?.["Qta EVA"] || 0, 10)}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default SalesChart;